/*
 * Multi-take uploads on the N3 Patient Timeline (lead decisions D1 / D2,
 * 10-06). Athena often holds several files for ONE thing: an ECG uploaded
 * once per take (patient 14106: three "electrocardiogram" results filed in
 * the same second, one observation time), the images of one study. They are
 * one group when they have the same category, type and Athena title (their
 * own description, else the type's default title; never the AI label), were
 * filed within 120 s of each other (every pair: the group spans at most
 * 120 s) and have the same observation time whenever both have one. Never by
 * day alone: two consult notes filed the same day at 12:41 and 15:07, or
 * untitled results of different observation dates scanned minutes apart,
 * stay separate.
 *
 * The server sends its groups on the Follow up row (doc.members, filing
 * order); those win for their files. Everything else (an older server, or a
 * document outside Follow up's window) is grouped here by the same rule.
 * The timeline lists a group as ONE row, "Electrocardiogram ×3", that opens
 * the newest file; while a Follow up row offers the group, every member is
 * hidden from the timeline (CareIQTabN3 coveredByFollowUp).
 * Pure: no React, no network. The legacy tab does not use it.
 */

export const TAKE_WINDOW_MS = 120 * 1000;

const MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;
const ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

// A filing / observation time in ms since the epoch, or null (absent, a date
// alone, unparseable). ISO with or without an offset (none = UTC, as the
// server reads it) and Athena's MM/DD/YYYY HH:MM[:SS] [AM|PM].
export const instantOf = (v) => {
  const s = String(v == null ? "" : v).trim();
  if (s.length < 13) return null;
  const m = MDY.exec(s);
  if (m) {
    let h = Number(m[4]);
    if (m[7]) h = (h % 12) + (/pm/i.test(m[7]) ? 12 : 0);
    return Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]), h, Number(m[5]), Number(m[6] || 0));
  }
  if (!ISO.test(s)) return null;
  const iso = s.replace(" ", "T");
  const t = Date.parse(OFFSET.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(t) ? null : t;
};

// The observation time (an instant, else its own text), or null.
const obsOf = (doc) => {
  const raw = String((doc && doc.observationdatetime) || "").trim();
  if (!raw) return null;
  const t = instantOf(raw);
  return t != null ? t : raw;
};

export const docCategory = (doc) => {
  const isClinical = doc.documentclass === "CLINICALDOCUMENT" || !!doc.clinicaldocumentid;
  const isEncounter = doc.documentclass === "ENCOUNTERDOCUMENT" || !!doc.encounterdocumentid;
  return isEncounter ? "encounterdocument" : isClinical ? "clinicaldocument" : "imagingresult";
};

export const docIdOf = (doc) => String((doc && (doc.imagingresultid || doc.encounterdocumentid || doc.clinicaldocumentid)) || "");

const GENERIC = new Set(["other", "oth", "clinical note"]);

// The document's own Athena title (the server's rule): its description, else
// the type's default title.
export const ownTitle = (doc, cat = docCategory(doc)) => {
  const desc = String(doc.documentdescription || "").trim();
  if (desc && !GENERIC.has(desc.toLowerCase())) return desc;
  const sub = String(doc.documentsubclass || doc.documenttype || "").toUpperCase();
  if (cat === "imagingresult") return doc.documenttype && doc.documenttype !== "Other" ? `${doc.documenttype} result` : "Imaging result";
  if (sub.includes("URGENTCARE")) return "Urgent care / ED visit";
  if (sub.includes("ADMISSIONDISCHARGE")) return "Hospital discharge summary";
  if (sub.includes("CONSULT")) return "Consult note";
  if (sub.includes("OPERATIVE")) return "Operative note";
  return "Report";
};

const norm = (s) => String(s || "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
const idOrder = (id) => (/^\d+$/.test(id) ? [1, Number(id)] : [0, id]);
const filedCmp = (a, b) => {
  const ka = a.t != null ? a.t : -Infinity;
  const kb = b.t != null ? b.t : -Infinity;
  if (ka !== kb) return ka < kb ? -1 : 1;
  const [ta, va] = idOrder(a.id);
  const [tb, vb] = idOrder(b.id);
  if (ta !== tb) return ta - tb;
  if (va === vb) return 0;
  return va < vb ? -1 : 1;
};

// Athena's "entered in error": not a record (the server's Follow up drops
// these listings first; the timeline does not list them either, so an
// erroneous take never sits next to its upload's row, nor is the file a
// group opens).
export const isEnteredInError = (doc) => String((doc && doc.status) || "").trim().toUpperCase() === "ENTERED-IN-ERROR";

// Each document once: entered-in-error listings are left out first (a
// document listed both ways keeps its live listing), then the same id listed
// twice, and an imaging result listed by its FHIR id next to its Athena id
// (fhir_resource_id), keep the first / the Athena entry.
export const listedOnce = (all) => {
  const entries = all.filter((e) => !isEnteredInError(e.doc));
  const athenaFhir = new Set(
    entries.filter((e) => e.cat === "imagingresult" && /^\d+$/.test(e.id) && e.doc.fhir_resource_id).map((e) => String(e.doc.fhir_resource_id)),
  );
  const seen = new Set();
  return entries.filter((e) => {
    if (e.cat === "imagingresult" && !/^\d+$/.test(e.id) && athenaFhir.has(e.id)) return false;
    const k = `${e.cat}:${e.id}`;
    if (!e.id || !seen.has(k)) {
      seen.add(k);
      return true;
    }
    return false;
  });
};

// The server's groups: [{cat, members: [ids, filing order]}] from Follow up rows.
export const serverGroups = (rows) =>
  (rows || [])
    .filter((r) => r && r.kind === "fu_doc" && r.doc && Array.isArray(r.doc.members) && r.doc.members.length > 1)
    .map((r) => ({ cat: r.doc.category, members: r.doc.members.map(String) }));

/*
 * entries: [{doc, cat, id, day ("YYYY-MM-DD"), ...}] (each kept as is).
 * Returns the groups, each {entries (filing order), newest, members (ids),
 * server (true when Follow up's row defined it)}, singletons included, in
 * the entries' first-seen order.
 */
export const groupTakes = (entries, groups = []) => {
  const list = listedOnce(entries || []).map((e) => ({
    ...e, t: instantOf(e.doc.createddatetime), obs: obsOf(e.doc), title: norm(ownTitle(e.doc, e.cat)),
    type: String(e.doc.documentsubclass || e.doc.documenttype || "").toUpperCase(),
  }));
  const byKey = new Map(list.map((e) => [`${e.cat}:${e.id}`, e]));
  const owner = new Map();
  const out = [];
  groups.forEach((g) => {
    const found = g.members.map((id) => byKey.get(`${g.cat}:${id}`)).filter((e) => e && !owner.has(e));
    if (found.length < 2) return;
    const grp = { entries: found, server: true };
    found.forEach((e) => owner.set(e, grp));
  });
  const buckets = new Map();
  list.forEach((e) => {
    if (owner.has(e)) return;
    const k = [e.cat, e.type, e.title, e.day].join("|");
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(e);
  });
  buckets.forEach((bucket) => {
    const open = [];
    [...bucket].sort(filedCmp).forEach((e) => {
      const home = e.t == null ? null : open.find((g) => g.t0 != null && e.t - g.t0 >= 0 && e.t - g.t0 <= TAKE_WINDOW_MS
        && (e.obs == null || g.obs == null || e.obs === g.obs));
      if (home) {
        home.entries.push(e);
        if (home.obs == null) home.obs = e.obs;
      } else {
        open.push({ t0: e.t, obs: e.obs, entries: [e], server: false });
      }
    });
    open.forEach((g) => g.entries.forEach((e) => owner.set(e, g)));
  });
  const emitted = new Set();
  list.forEach((e) => {
    const g = owner.get(e);
    if (emitted.has(g)) return;
    emitted.add(g);
    out.push({
      entries: g.entries,
      newest: g.entries[g.entries.length - 1],
      members: g.entries.map((x) => x.id),
      server: g.server,
    });
  });
  return out;
};

const capFirst = (s) => (/^[a-z]/.test(s) ? s[0].toUpperCase() + s.slice(1) : s);

// The label of a group's timeline row: the members' shared label (an AI label
// only when every file has the same one; never a generic "Other"), else the
// newest file's own Athena title; "Electrocardiogram ×3", "Imaging result ×3".
export const groupLabel = (group, labelOf) => {
  const labels = group.entries.map((e) => String(labelOf(e) || "").trim());
  const same = labels.every((l) => l && norm(l) === norm(labels[0])) && !GENERIC.has(norm(labels[0]));
  const name = same ? labels[labels.length - 1] : ownTitle(group.newest.doc, group.newest.cat);
  return group.entries.length > 1 ? `${capFirst(name)} ×${group.entries.length}` : name;
};
